import { Injectable } from '@angular/core';
import { CleanedDomainData, ParsedImportFile } from '../types/domain-management.types';

@Injectable({
    providedIn: 'root',
})
export class DomainFileParserService {

    /**
     * Parse uploaded JSON file and validate structure
     */
    parseImportFile (file: File): Promise<ParsedImportFile> {
        return new Promise((resolve) => {
            const reader = new FileReader();

            reader.onload = (e): void => {
                try {
                    const content = e.target?.result as string;
                    const data = JSON.parse(content);

                    const result = this.validateImportData(data);
                    resolve(result);
                } catch (error) {
                    const errorResult: ParsedImportFile = {
                        isValid: false,
                        domains: [],
                        totalCount: 0,
                        errors: [`Invalid JSON file: ${error?.message || 'Parse error'}`],
                    };
                    resolve(errorResult);
                }
            };

            reader.onerror = (): void => {
                const errorResult: ParsedImportFile = {
                    isValid: false,
                    domains: [],
                    totalCount: 0,
                    errors: ['Failed to read file'],
                };
                resolve(errorResult);
            };

            reader.readAsText(file);
        });
    }

    /**
     * Validate the structure of imported data
     */
    private validateImportData (data: unknown): ParsedImportFile {
        const errors: string[] = [];
        let domains: CleanedDomainData[] = [];

        if (!data || typeof data !== 'object') {
            errors.push('Invalid file format: Expected JSON object');
        } else {
            const parsedData = data as Record<string, unknown>;

            if (!Array.isArray(parsedData.domains)) {
                errors.push('Invalid file format: Missing "domains" array');
            } else {
                domains = parsedData.domains;

                domains.forEach((domain, index) => {
                    const domainErrors = this.validateDomainDataStructure(domain, index);
                    errors.push(...domainErrors);
                });
            }

            if (parsedData.domainCount && typeof parsedData.domainCount === 'number' && parsedData.domainCount !== domains.length) {
                errors.push(`Domain count mismatch: Expected ${parsedData.domainCount}, found ${domains.length}`);
            }
        }

        const parsedDataForMeta = data as Record<string, unknown>;
        return {
            isValid: errors.length === 0,
            domains: domains,
            totalCount: domains.length,
            errors: errors,
            exportedAt: typeof parsedDataForMeta?.exportedAt === 'string' ? parsedDataForMeta.exportedAt : undefined,
            exportedBy: typeof parsedDataForMeta?.exportedBy === 'string' ? parsedDataForMeta.exportedBy : undefined,
        };
    }

    /**
     * Validate individual domain data structure
     */
    private validateDomainDataStructure (domain: unknown, index: number): string[] {
        const errors: string[] = [];
        const prefix = `Domain ${index + 1}:`;

        if (!domain || typeof domain !== 'object') {
            errors.push(`${prefix} Invalid domain format`);
            return errors;
        }

        const domainData = domain as Record<string, unknown>;

        if (!domainData.url || typeof domainData.url !== 'string') {
            errors.push(`${prefix} Missing or invalid URL`);
        }

        if (!domainData.title || typeof domainData.title !== 'string') {
            errors.push(`${prefix} Missing or invalid title`);
        }

        if (domainData.url && typeof domainData.url === 'string') {
            try {
                new URL(domainData.url);
            } catch {
                errors.push(`${prefix} Invalid URL format: ${domainData.url}`);
            }
        }

        if (domainData.path_constraints && !Array.isArray(domainData.path_constraints)) {
            errors.push(`${prefix} path_constraints must be an array`);
        }

        if (domainData.link_excludes && !Array.isArray(domainData.link_excludes)) {
            errors.push(`${prefix} link_excludes must be an array`);
        }

        if (domainData.domain_groups && !Array.isArray(domainData.domain_groups)) {
            errors.push(`${prefix} domain_groups must be an array`);
        }

        if (domainData.domain_users && !Array.isArray(domainData.domain_users)) {
            errors.push(`${prefix} domain_users must be an array`);
        }

        return errors;
    }
}
